import type { NavigateFunction, To } from 'react-router';
import { useNavigate } from 'react-router';
import { ContextKindKey } from '@gonfalon/contexts/types';
import {
  disabledRules,
  isFlagStatusEnabled,
  shouldCleanupAfterFlagDeletion,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import type { FeatureFlag } from '@gonfalon/flags/types';
import type { Map, OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
import { List } from '@gonfalon/immutable-shim/immutableShim';
import type { FieldPath } from '@gonfalon/legacy-forms/formUtils';
import type { ImmutableServerError } from '@gonfalon/legacy-http-client/httpUtils';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { getAutomatedReleasesQuery } from '@gonfalon/rest-api/queries/automatedReleases';
import { getExperimentsQuery } from '@gonfalon/rest-api/queries/experiments';
import { getFeatureFlagQuery, getFeatureFlagsQuery } from '@gonfalon/rest-api/queries/flags';
import type { JSONPatch } from '@gonfalon/rest-api/types';
import { defer, isArray, noop } from 'lodash';
import nullthrows from 'nullthrows';

import { fetchScheduledChangesForFlag } from '#actions/scheduledChanges';
import { flagLinks as flagLinksActionTypes } from '#actionTypes/flagLinks';
import type { ConfigVariationIds } from '#components/targeting/guarded-rollout/types';
import { useDispatch } from '#hooks/useDispatch';
import { flagManagerSelector } from '#reducers/flagManager';
import type { FlagAndEnvKeys } from '#reducers/flags';
import {
  flagListRequestSelector,
  flagRequestByKeySelector,
  flagsSelector,
  flagStatusesByProjectKeySelector,
} from '#reducers/flags';
import { projectFlagSettingsFormSelector } from '#reducers/flagSettings';
import { currentEnvironmentSelector } from '#reducers/projects';
import { ruleExclusionFormSelector } from '#reducers/ruleExclusion';
import type { GetState, GlobalDispatch, GlobalState } from '#reducers/state';
import { reportAccesses } from '#sources/AccountAPI';
// oxlint-disable-next-line import/no-namespace
import * as FlagAPI from '#sources/FlagAPI';
import { accessResponseDeniesUpdateTags } from '#utils/access/accessResponseDeniesUpdateTags';
import { makeResourceSpec } from '#utils/access/makeResourceSpec';
import type { DeniedAccess } from '#utils/access/types';
import type { EventLocation } from '#utils/analyticsUtils';
import type { Clause } from '#utils/clause/records/Clause';
import { createClause } from '#utils/clause/records/Clause';
import type { FlagFilters } from '#utils/flagFilters/records/FlagFilters';
import { clearPercentageRollout } from '#utils/flags/clearPercentageRollout';
import { getControlAndTestVariations } from '#utils/flags/getControlAndTestVariations';
import { idOrKey } from '#utils/flags/idOrKey';
import type { Fallthrough } from '#utils/flags/records/Fallthrough';
import type { Flag } from '#utils/flags/records/Flag';
import type { ReleasePhaseConfig } from '#utils/flags/records/ReleasePhaseConfig';
import { createRule, Rule } from '#utils/flags/records/Rule';
import type { RuleExclusionSettings, RuleExclusionSettingsProps } from '#utils/flags/records/RuleExclusionSettings';
import type { Variation } from '#utils/flags/records/Variation';
import { removePendingGuardedRollout } from '#utils/flags/removePendingGuardedRollout';
import { setDefaultVariationForRollout } from '#utils/flags/setDefaultVariationForRollout';
import type { FlagStatus } from '#utils/flagStatusUtils';
import { OnOffInstructionKind } from '#utils/instructions/onOff/types';
import { RuleInstructionKind } from '#utils/instructions/rules/constants';
import type { SemanticInstruction } from '#utils/instructions/shared/types';
import type { GenerateActionType } from '#utils/reduxUtils';
import type { Request } from '#utils/requestUtils';
import { dotSeparateKey } from '#utils/stringUtils';

import { deleteAllTriggersAction } from './integrations';

const asArray = <T>(field: T | T[]) => (isArray(field) ? field : [field]);

const requestFlagStatus = (projectKey: string, flagKey: string, environmentKey: string, parentLink: string) =>
  ({
    type: 'flags/REQUEST_FLAG_STATUS',
    projectKey,
    flagKey,
    environmentKey,
    parentLink,
  }) as const;

const requestFlagStatusDone = (
  projectKey: string,
  flagKey: string,
  environmentKey: string,
  parentLink: string,
  status?: FlagStatus,
) =>
  ({
    type: 'flags/RECEIVE_FLAG_STATUS',
    projectKey,
    flagKey,
    environmentKey,
    parentLink,
    status,
  }) as const;

const requestFlagStatusFailed = (
  projectKey: string,
  flagKey: string,
  environmentKey: string,
  parentLink: string,
  error: ImmutableServerError,
) =>
  ({
    type: 'flags/REQUEST_FLAG_STATUS_FAILED',
    projectKey,
    flagKey,
    environmentKey,
    parentLink,
    error,
  }) as const;

function fetchFlagStatusByKey(projectKey: string, flagKey: string, environmentKey: string) {
  const parentLink = `/api/v2/flags/${projectKey}/${flagKey}`;
  return (dispatch: GlobalDispatch) => {
    dispatch(requestFlagStatus(projectKey, flagKey, environmentKey, parentLink));
    return new Promise((resolve, reject) => {
      FlagAPI.getFlagStatusesByEnvKeys(projectKey, flagKey, [environmentKey])
        .then((statuses) => {
          dispatch(
            requestFlagStatusDone(projectKey, flagKey, environmentKey, parentLink, statuses.get(environmentKey)),
          );

          resolve(statuses);
        })
        .catch((error) => {
          dispatch(requestFlagStatusFailed(projectKey, flagKey, environmentKey, parentLink, error));
          reject(error);
        });
    });
  };
}

function shouldFetchFlagStatus(state: GlobalState, projectKey: string, flagKey: string, environmentKey: string) {
  if (!isFlagStatusEnabled()) {
    return false;
  }

  const statuses = flagStatusesByProjectKeySelector(state, { projectKey });

  if (!statuses) {
    return true;
  }

  const status = statuses.getIn([environmentKey, flagKey]);

  return status ? !status.get('lastFetched') && !status.get('isFetching') : true;
}

function fetchFlagStatusByKeyIfNeeded(projectKey: string, flagKey: string, environmentKey: string) {
  return (dispatch: GlobalDispatch, getState: GetState) => {
    if (shouldFetchFlagStatus(getState(), projectKey, flagKey, environmentKey)) {
      return dispatch(fetchFlagStatusByKey(projectKey, flagKey, environmentKey));
    } else {
      return Promise.resolve();
    }
  };
}

const requestFlags = (
  filters: FlagFilters | undefined,
  projectKey: string,
  environmentKeys: string[],
  controller?: AbortController,
) =>
  ({
    type: 'flags/REQUEST_FLAGS',
    filters,
    projectKey,
    environmentKeys,
    controller,
  }) as const;

const requestFlagsDone = (
  response: OrderedMap<string, Flag>,
  filters: FlagFilters | undefined,
  currentFlagKey: string,
  isSummaryRep: boolean,
  projectKey: string,
  environmentKeys: string[],
  options: FlagAPI.FetchFlagsOptions,
) =>
  ({
    type: 'flags/RECEIVE_FLAGS',
    response,
    filters,
    currentFlagKey,
    isSummaryRep,
    projectKey,
    environmentKeys,
    options,
  }) as const;

const requestFlagsFailed = (
  projectKey: string,
  environmentKeys: string[],
  error: ImmutableServerError,
  filters: FlagFilters | undefined,
) =>
  ({
    type: 'flags/REQUEST_FLAGS_FAILED',
    projectKey,
    environmentKeys,
    error,
    filters,
  }) as const;

const requestFlagsAborted = (
  projectKey: string,
  environmentKeys: string[],
  signal: AbortSignal,
  filters: FlagFilters | undefined,
) =>
  ({
    type: 'flags/REQUEST_FLAGS_ABORTED',
    projectKey,
    environmentKeys,
    signal,
    filters,
  }) as const;

function fetchFlags(
  projectKey: string,
  environmentKeys: string[] = [],
  filters?: FlagFilters,
  options: FlagAPI.FetchFlagsOptions = {},
  requestOptions: FlagAPI.FetchFlagsRequestOptions = {},
) {
  return (dispatch: GlobalDispatch, getState: GetState) => {
    if (options.refetch) {
      const state = getState();
      const { isFetching, controller } = flagsSelector(state, projectKey).listRequest.toJS() as Request;
      if (isFetching) {
        try {
          controller && controller.abort('refetching flags');
        } catch {
          // ignore
        }
      }
    }
    dispatch(requestFlags(filters, projectKey, environmentKeys, requestOptions.controller));
    return new Promise((resolve, reject) => {
      FlagAPI.getFlags(projectKey, environmentKeys, filters, options)
        .then((response) => {
          const state = getState();
          const currentFlagKey = flagManagerSelector(state).getIn(['original', 'key']) as string;
          dispatch(requestFlagsDone(response, filters, currentFlagKey, true, projectKey, environmentKeys, options));
          resolve(response);
        })
        .catch((error) => {
          const { signal } = requestOptions;

          if (signal && signal.aborted) {
            // Anything more useful can do here?
            dispatch(requestFlagsAborted(projectKey, environmentKeys, error, filters));
            return;
          }
          dispatch(requestFlagsFailed(projectKey, environmentKeys, error, filters));
          reject(error);
        });
    });
  };
}

function shouldFetchFlags(state: GlobalState, options: { refetch?: boolean } = {}) {
  const req = flagListRequestSelector(state);
  return !req || (!req.lastFetched && !req.isFetching) || options.refetch;
}

function fetchFlagsIfNeeded(
  projectKey: string,
  environmentKeys: string[] = [],
  filters?: FlagFilters,
  options: FlagAPI.FetchFlagsOptions = {},
  requestOptions: FlagAPI.FetchFlagsRequestOptions = {},
) {
  const queryClient = getQueryClient();
  const isFetching = queryClient.isFetching({ queryKey: getFeatureFlagsQuery({ projectKey }).queryKey });
  return async (dispatch: GlobalDispatch, getState: GetState) => {
    if (shouldFetchFlags(getState(), options)) {
      if (isFetching) {
        await queryClient.cancelQueries(getFeatureFlagsQuery({ projectKey }));
      }
      return dispatch(fetchFlags(projectKey, environmentKeys, filters, options, requestOptions));
    } else {
      return Promise.resolve();
    }
  };
}

const requestFlagStatuses = (projectKey: string, environmentKey: string) =>
  ({
    type: 'flags/REQUEST_FLAG_STATUSES',
    projectKey,
    environmentKey,
  }) as const;

const requestFlagStatusesDone = (projectKey: string, environmentKey: string, response: Map<string, FlagStatus>) =>
  ({
    type: 'flags/RECEIVE_FLAG_STATUSES',
    projectKey,
    environmentKey,
    response,
  }) as const;

const requestFlagStatusesFailed = (projectKey: string, environmentKey: string, error: ImmutableServerError) =>
  ({
    type: 'flags/REQUEST_FLAG_STATUSES_FAILED',
    projectKey,
    environmentKey,
    error,
  }) as const;

function fetchFlagStatusesForEnvironment(projectKey: string, environmentKey: string) {
  return (dispatch: GlobalDispatch) => {
    if (!isFlagStatusEnabled()) {
      return;
    }

    dispatch(requestFlagStatuses(projectKey, environmentKey));
    return FlagAPI.getAllFlagStatusesForEnvironment(projectKey, environmentKey)
      .then((response) => dispatch(requestFlagStatusesDone(projectKey, environmentKey, response)))
      .catch((error) => dispatch(requestFlagStatusesFailed(projectKey, environmentKey, error)));
  };
}

const requestFlagStatusesByEnvKeysAndFlagKeys = (projectKey: string, environmentKeys: string[], flagKeys: string[]) =>
  ({
    type: 'flags/REQUEST_FLAG_STATUSES_BY_ENVS_AND_FLAGS',
    projectKey,
    environmentKeys,
    flagKeys,
  }) as const;

const requestFlagStatusesByEnvKeysAndFlagKeysDone = (
  projectKey: string,
  environmentKeys: string[],
  flagKeys: string[],
  response: Map<string, FlagStatus>,
) =>
  ({
    type: 'flags/RECEIVE_FLAG_STATUSES_BY_ENVS_AND_FLAGS',
    projectKey,
    environmentKeys,
    flagKeys,
    response,
  }) as const;

const requestFlagStatusesByEnvKeysAndFlagKeysFailed = (
  projectKey: string,
  environmentKeys: string[],
  flagKeys: string[],
  error: ImmutableServerError,
) =>
  ({
    type: 'flags/REQUEST_FLAG_STATUSES_BY_ENVS_AND_FLAGS_FAILED',
    projectKey,
    environmentKeys,
    flagKeys,
    error,
  }) as const;

const requestFlag = (flagKey: string, projectKey: string, environmentKey: string) =>
  ({
    type: 'flags/REQUEST_FLAG',
    flagKey,
    projectKey,
    environmentKey,
  }) as const;

export const requestFlagDone = (flag: Flag, flagKey: string, projectKey: string, environmentKey: string) =>
  ({
    type: 'flags/RECEIVE_FLAG',
    flag,
    flagKey,
    projectKey,
    environmentKey,
  }) as const;

export const requestFlagFailed = (
  flagKey: string,
  error: ImmutableServerError,
  projectKey: string,
  environmentKey: string,
) =>
  ({
    type: 'flags/REQUEST_FLAG_FAILED',
    flagKey,
    error,
    projectKey,
    environmentKey,
  }) as const;

function fetchFlag(
  projectKey: string,
  flagKey: string,
  environmentKey: string,
  options: FlagAPI.FetchFlagByKeyOptions & { skipRequestDispatch?: boolean } = {},
) {
  return (dispatch: GlobalDispatch) => {
    // set `skipRequestDispatch` to true if you want to update a flag without putting it in the "fetching" state
    if (!options.skipRequestDispatch) {
      dispatch(requestFlag(flagKey, projectKey, environmentKey));
    }
    return new Promise<Flag>((resolve, reject) => {
      const fetchOptions = { ...options };
      delete fetchOptions.skipRequestDispatch;
      FlagAPI.getFlagByKey(projectKey, flagKey, environmentKey ? [environmentKey] : [], fetchOptions)
        .then((flag) => {
          dispatch(requestFlagDone(flag, flagKey, projectKey, environmentKey));
          resolve(flag);
        })
        .catch((error) => {
          dispatch(requestFlagFailed(flagKey, error, projectKey, environmentKey));
          reject(error);
        });
    });
  };
}

function shouldFetchFlag(state: GlobalState, projectKey: string, flagKey: string) {
  const req = flagRequestByKeySelector(state, { flagKey });
  return req ? (!req.lastFetched || req.isSummaryRep) && !req.isFetching : true;
}

function fetchFlagIfNeeded(
  projectKey: string,
  flagKey: string,
  environmentKey: string,
  options?: FlagAPI.FetchFlagByKeyOptions,
) {
  return (dispatch: GlobalDispatch, getState: GetState) => {
    if (shouldFetchFlag(getState(), projectKey, flagKey)) {
      return dispatch(fetchFlag(projectKey, flagKey, environmentKey, options));
    } else {
      return Promise.resolve();
    }
  };
}

function fetchFlagWithStatus(
  projectKey: string,
  flagKey: string,
  environmentKey: string,
  options?: FlagAPI.FetchFlagByKeyOptions,
) {
  return (dispatch: GlobalDispatch) =>
    dispatch(fetchFlagIfNeeded(projectKey, flagKey, environmentKey, options)).then(() => {
      defer(() => dispatch(fetchFlagStatusByKeyIfNeeded(projectKey, flagKey, environmentKey)));
    });
}

function fetchFlagByKeyForCurrentProject(
  projectKey: string,
  flagKey: string,
  environmentKey: string,
  options?: FlagAPI.FetchFlagByKeyOptions,
) {
  return (dispatch: GlobalDispatch) =>
    new Promise((resolve, reject) => {
      const action = isFlagStatusEnabled()
        ? fetchFlagWithStatus(projectKey, flagKey, environmentKey, options)
        : fetchFlagIfNeeded(projectKey, flagKey, environmentKey, options);
      dispatch(action).then(resolve, reject);
    });
}

export const requestDeleteFlagDone = (flag: Flag, projectKey: string) =>
  ({
    type: 'flags/DELETE_FLAG_DONE',
    flag,
    projectKey,
  }) as const;

export function useDeleteFlag() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return async (
    projectKey: string,
    flag: Flag,
    options: {
      pathOnSuccess?: To;
    } = {},
  ) => {
    await FlagAPI.deleteFlag(flag);
    // Invalidate the flags list since we've deleted a flag
    // associated story: https://launchdarkly.atlassian.net/jira/software/c/projects/REL/boards/204?selectedIssue=REL-3048
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getFeatureFlagsQuery({ projectKey }).queryKey });
    if (options.pathOnSuccess) {
      // adding a replace because page breaks when users go back to a flag they just deleted using the browser back button
      await navigate(options.pathOnSuccess, {
        replace: true,
      });
    }
    dispatch(requestDeleteFlagDone(flag, projectKey));
    if (shouldCleanupAfterFlagDeletion()) {
      dispatch({ type: flagLinksActionTypes.DELETE_ALL_FLAG_LINKS, flag, flagKey: flag.key });
      dispatch(deleteAllTriggersAction());
    }
  };
}

export type UpdateFlagOptions = {
  envKey?: string;
  environmentKey?: string;
  pathOnSuccess?: To;
  pathOnError?: string;
  navigate?: NavigateFunction;
  fullPageReload?: boolean;
  shouldRefetchScheduledFlagChanges?: boolean;
  eventLocation?: EventLocation;
  comment?: string;
  patchOperations?: JSONPatch | { comment: string; patch: JSONPatch };
  removeEmptyFields?: boolean;
  computedSemanticInstructions?: List<SemanticInstruction>;
  successMessage?: string;
};

type UpdateRequest<T extends string> = (
  oldFlag: Flag,
  newFlag: Flag,
) => Readonly<{
  type: T;
  oldFlag: Flag;
  newFlag: Flag;
}>;

type UpdateRequestDone<T extends string> = (
  flag: Flag,
  projectKey: string,
  options: UpdateFlagOptions,
) => Readonly<{
  type: T;
  flag: Flag;
  projectKey: string;
  options: UpdateFlagOptions;
}>;

type UpdateRequestFailed<T extends string> = (
  flag: Flag,
  error: ImmutableServerError,
) => Readonly<{
  type: T;
  flag: Flag;
  error: ImmutableServerError;
}>;

export const updateFlagBase =
  <T extends string>(updateActionCreators: [UpdateRequest<T>, UpdateRequestDone<T>, UpdateRequestFailed<T>]) =>
  (projectKey: string, environmentKey: string, oldFlag: Flag, newFlag: Flag, options: UpdateFlagOptions = {}) => {
    const [requestActionCreator, successActionCreator, failureActionCreator] = updateActionCreators;
    return async (dispatch: GlobalDispatch): Promise<Flag> => {
      dispatch(requestActionCreator(oldFlag, newFlag));

      const { computedSemanticInstructions: pendingInstructions = List() } = options;
      // updateFlag is called from either Targeting or Variations with its own respective instructions
      // If the patch is marked invalid, or contains no instructions, we want to do a regular JSON patch instead.
      const isValidSemanticPatch = pendingInstructions.size > 0;

      const queryClient = getQueryClient();

      return new Promise<Flag>((resolve, reject) => {
        // If we have a semantic patch to send and the correct flag is turned on, send only the semantic patch.
        //const promise = FlagAPI.updateFlag(oldFlag, newFlag, optionsWithSemanticPatch);
        const promise = isValidSemanticPatch
          ? FlagAPI.updateFlagSemantically(
              projectKey,
              newFlag,
              options.envKey || options.environmentKey,
              pendingInstructions,
              options,
            )
          : FlagAPI.updateFlag(projectKey, oldFlag, newFlag, options);

        promise
          .then(async (flag) => {
            options.shouldRefetchScheduledFlagChanges &&
              (await dispatch(
                fetchScheduledChangesForFlag({
                  flagKey: newFlag.key,
                  projKey: projectKey,
                  envKey: environmentKey,
                }),
              ));
            if (options.pathOnSuccess) {
              if (options.fullPageReload) {
                window.location.href =
                  typeof options.pathOnSuccess === 'object'
                    ? `${options.pathOnSuccess.pathname}?${options.pathOnSuccess.search}`
                    : options.pathOnSuccess;
                return;
              }
              await options.navigate?.(options.pathOnSuccess);
            }

            dispatch(successActionCreator(flag, projectKey, options));

            if (isFlagStatusEnabled()) {
              defer(async () => {
                try {
                  await dispatch(fetchFlagStatusByKeyIfNeeded(projectKey, flag.key, environmentKey));
                } catch {
                  // noop
                }
              });
            }

            // Remove cached flag query data so React Query-based pages
            // (e.g. the targeting page rewrite) fetch fresh data on next mount.
            queryClient.removeQueries(getFeatureFlagQuery({ projectKey, featureFlagKey: flag.key }));

            const hasMeasuredRolloutInstructions = pendingInstructions.some(
              (instruction) =>
                instruction.kind === OnOffInstructionKind.UPDATE_FALLTHROUGH_WITH_MEASURED_ROLLOUT_V2 ||
                instruction.kind === RuleInstructionKind.UPDATE_RULE_WITH_MEASURED_ROLLOUT_V2 ||
                instruction.kind === OnOffInstructionKind.STOP_MEASURED_ROLLOUT_ON_FALLTHROUGH ||
                instruction.kind === RuleInstructionKind.STOP_MEASURED_ROLLOUT_ON_RULE,
            );

            if (hasMeasuredRolloutInstructions) {
              await dispatch(fetchFlag(projectKey, flag.key, environmentKey));
              await queryClient.invalidateQueries({
                queryKey: getExperimentsQuery.partialQueryKey({
                  projectKey,
                  environmentKey,
                }),
              });
              await queryClient.invalidateQueries({
                queryKey: getAutomatedReleasesQuery.partialQueryKey({ projectKey, flagKey: flag.key }),
              });
            }

            resolve(flag);
          })
          .catch(async (error) => {
            if (options.pathOnError && options.navigate) {
              await options.navigate(options.pathOnError);
            }
            dispatch(failureActionCreator(newFlag, error));
            reject(error);
          });
      });
    };
  };

export const createUpdateFlagRequestActionCreator =
  <T extends string>(type: T) =>
  (oldFlag: Flag, newFlag: Flag) =>
    ({
      type,
      oldFlag,
      newFlag,
    }) as const;

export const createUpdateFlagRequestDoneActionCreator =
  <T extends string>(type: T) =>
  (flag: Flag, projectKey: string, options: UpdateFlagOptions = {}) =>
    ({
      type,
      flag,
      projectKey,
      options,
    }) as const;

export const createUpdateFlagRequestFailedActionCreator =
  <T extends string>(type: T) =>
  (flag: Flag, error: ImmutableServerError) =>
    ({
      type,
      flag,
      error,
    }) as const;

const updateFlagRequest = createUpdateFlagRequestActionCreator('flags/UPDATE_FLAG');

const updateFlagRequestDone = createUpdateFlagRequestDoneActionCreator('flags/UPDATE_FLAG_DONE');

const updateFlagRequestFailed = createUpdateFlagRequestFailedActionCreator('flags/UPDATE_FLAG_FAILED');

const updateFlag = updateFlagBase([updateFlagRequest, updateFlagRequestDone, updateFlagRequestFailed]);

const updateProjectFlagSettingsRequest = createUpdateFlagRequestActionCreator('flags/UPDATE_PROJECT_FLAG_SETTINGS');

export const updateProjectFlagSettingsRequestDone = createUpdateFlagRequestDoneActionCreator(
  'flags/UPDATE_PROJECT_FLAG_SETTINGS_DONE',
);

const updateProjectFlagSettingsRequestFailed = createUpdateFlagRequestFailedActionCreator(
  'flags/UPDATE_PROJECT_FLAG_SETTINGS_FAILED',
);

const updateProjectFlagSettings = updateFlagBase([
  updateProjectFlagSettingsRequest,
  updateProjectFlagSettingsRequestDone,
  updateProjectFlagSettingsRequestFailed,
]);

const updateRuleExclusionRequest = createUpdateFlagRequestActionCreator('flags/UPDATE_RULE_EXCLUSION');

const updateRuleExclusionRequestDone = createUpdateFlagRequestDoneActionCreator('flags/UPDATE_RULE_EXCLUSION_DONE');

const updateRuleExclusionRequestFailed = createUpdateFlagRequestFailedActionCreator(
  'flags/UPDATE_RULE_EXCLUSION_FAILED',
);

const updateRuleExclusion = updateFlagBase([
  updateRuleExclusionRequest,
  updateRuleExclusionRequestDone,
  updateRuleExclusionRequestFailed,
]);

function selectOffVariation(flag: Flag, environmentKey: string, variation: Variation) {
  return {
    type: 'flags/CHANGE_FLAG_CONFIGURATION',
    flag: flag.setOffVariation(environmentKey, variation),
    environmentKey,
  } as const;
}

function clearOffVariation(flag: Flag, environmentKey: string) {
  return {
    type: 'flags/CHANGE_FLAG_CONFIGURATION',
    flag: flag.clearOffVariation(environmentKey),
    environmentKey,
  } as const;
}

function resetConfiguration() {
  return {
    type: 'flags/RESET_FLAG_CONFIGURATION',
  } as const;
}

function initializeConfigurationManager(flag: Flag) {
  return {
    type: 'flags/INITIALIZE_FLAG_CONFIG_MANAGER',
    flag,
  } as const;
}

const editProjectFlagMaintainerAction = (flag: Flag) =>
  ({
    type: 'flags/EDIT_PROJECT_FLAG_MAINTAINER',
    flag,
  }) as const;

const initializeProjectFlagMaintainer = (flag: Flag) =>
  ({
    type: 'flags/INITIALIZE_PROJECT_FLAG_MAINTAINER',
    flag,
  }) as const;

const editProjectFlagSettingsAction = (field: FieldPath, flag: Flag) =>
  ({
    type: 'flags/EDIT_PROJECT_FLAG_SETTINGS',
    field,
    flag,
  }) as const;

const checkAccessResource = (willRemoveEditingAbility: boolean) =>
  ({
    type: 'flags/CHECK_ACCESS_RESOURCE',
    willRemoveEditingAbility,
  }) as const;

function editProjectFlagSettings(
  projectKey: string,
  environmentKey: string,
  field: FieldPath,
  // This is tricky: value can be any valid property type on flag, including on nested properties (since field can point to nested properties).
  // oxlint-disable-next-line typescript/no-explicit-any
  value: any,
  options: { flagKey?: string; oldFlag?: Flag; isAccessWarningEnabled?: boolean; isNewCustomProperty?: boolean } = {},
) {
  return (dispatch: GlobalDispatch, getState: GetState) => {
    const form = projectFlagSettingsFormSelector(getState());
    const validatedField = asArray(field);
    let modified = form.modified.setIn(validatedField, value);
    // Autofill dot-separated key for custom properties
    if (
      options.isNewCustomProperty &&
      validatedField.length === 3 &&
      validatedField[2] === 'name' &&
      !form.wasChanged(['customProperties', validatedField[1], 'key'])
    ) {
      modified = modified.setIn(['customProperties', validatedField[1], 'key'], dotSeparateKey(value));
    }

    dispatch(editProjectFlagSettingsAction(field, modified));

    if (options.isAccessWarningEnabled && field === 'tags') {
      const flagKey = nullthrows(options.flagKey, 'the access warning needs the flag the tags belong to');
      const oldFlag = nullthrows(options.oldFlag, 'the access warning needs the flag as it was before the edit');
      const newResourceSpec = makeResourceSpec(
        projectKey,
        '*', // project settings affect all environments
        'flag',
        flagKey,
        value,
      );
      const oldAccessDenied = oldFlag.getConfiguration(environmentKey)?._access?.get('denied') || List<DeniedAccess>();

      reportAccesses(newResourceSpec).then((accesses) => {
        const willRemoveEditingAbility = accessResponseDeniesUpdateTags(accesses, newResourceSpec, oldAccessDenied);
        dispatch(checkAccessResource(willRemoveEditingAbility));
      }, noop);
    }
  };
}

const editRuleExclusionAction = (
  fieldNameOrRuleIndex: keyof RuleExclusionSettingsProps | number,
  ruleExclusionSettings: RuleExclusionSettings,
) =>
  ({
    type: 'flags/EDIT_RULE_EXCLUSION',
    fieldNameOrRuleIndex,
    ruleExclusionSettings,
  }) as const;

function editRuleExclusion(
  fieldNameOrRuleIndex: keyof RuleExclusionSettingsProps | number,
  value: RuleExclusionSettingsProps[keyof RuleExclusionSettingsProps],
  isCustomRule?: boolean,
) {
  return (dispatch: GlobalDispatch, getState: GetState) => {
    const form = ruleExclusionFormSelector(getState());
    let modified: RuleExclusionSettings;
    if (isCustomRule) {
      modified = form.modified.setIn(['rules', fieldNameOrRuleIndex, 'trackEvents'], value);
    } else {
      modified = form.modified.set(fieldNameOrRuleIndex, value);
    }

    return dispatch(editRuleExclusionAction(fieldNameOrRuleIndex, modified));
  };
}

const changeFlagConfiguration = (flag: Flag, environmentKey: string) =>
  ({
    type: 'flags/CHANGE_FLAG_CONFIGURATION',
    flag,
    environmentKey,
  }) as const;

function setFallthroughVariation(originalFlag: Flag, draftFlag: Flag, environmentKey: string, variation: Variation) {
  let newFlag = draftFlag.setFallthroughVariation(environmentKey, variation);

  newFlag = newFlag.setFallthroughReleasePhaseConfig(environmentKey, undefined);

  return changeFlagConfiguration(newFlag, environmentKey);
}

function setFallthroughRollout(
  flag: Flag,
  environmentKey: string,
  variation: Variation,
  weight: number,
  contextKind?: string,
) {
  let newFlag = flag.setFallthroughRollout(environmentKey, variation, weight, contextKind);

  if (flag.getFallthrough(environmentKey).measuredRolloutConfig) {
    newFlag = newFlag.setFallthroughReleasePhaseConfig(environmentKey, undefined);
  }
  return changeFlagConfiguration(newFlag, environmentKey);
}

function setFallthroughBucket(flag: Flag, environmentKey: string, bucketBy: string, contextKind?: string) {
  const newFlag = flag.setFallthroughBucket(
    environmentKey,
    bucketBy === 'key' && !contextKind ? undefined : bucketBy,
    contextKind,
  );

  return changeFlagConfiguration(newFlag, environmentKey);
}

function setProgressiveRolloutConfig(
  originalFlag: Flag,
  draftFlag: Flag,
  environmentKey: string,
  rule: Rule | Fallthrough,
) {
  let updatedDraftFlag = draftFlag;
  updatedDraftFlag = setDefaultVariationForRollout(originalFlag, updatedDraftFlag, environmentKey, rule);
  updatedDraftFlag = removePendingGuardedRollout(updatedDraftFlag, environmentKey);
  updatedDraftFlag = changeFlagConfiguration(updatedDraftFlag, environmentKey).flag;

  if (!draftFlag.isOn(environmentKey)) {
    updatedDraftFlag = updatedDraftFlag.updateConfiguration(environmentKey, (draftFlagConfig) =>
      draftFlagConfig.set('on', true),
    );
  }

  // Auto-enable the rule if it's disabled (rollouts can't run on disabled rules)
  if (disabledRules() && rule instanceof Rule && rule.disabled) {
    updatedDraftFlag = updatedDraftFlag.enableRule(environmentKey, rule);
  }

  return changeFlagConfiguration(updatedDraftFlag, environmentKey);
}

const modifySegmentRule = (segmentKey: string, rule: Rule) => (dispatch: GlobalDispatch, getState: GetState) => {
  const state = getState();
  const environment = currentEnvironmentSelector(state).get('entity');
  const envKey = environment.key;
  const contextKind = rule.clauses.get(0)?.contextKind || 'user';
  const clause = createClause({
    attribute: 'segmentMatch',
    op: 'segmentMatch',
    contextKind,
    values: List([segmentKey]),
  });

  let flag = flagManagerSelector(state).get('modified') as Flag;
  flag = flag.updateConfiguration(envKey, (config) =>
    config.update('rules', (rules) =>
      rules.update(
        rules.findIndex((r) => r._key === rule._key),
        (r) => {
          return r.set('clauses', List([clause]));
        },
      ),
    ),
  );

  dispatch(changeFlagConfiguration(flag, envKey));
};

const addSegmentRule =
  (segmentKey: string, variation: Variation, contextKind: string = 'user') =>
  (dispatch: GlobalDispatch, getState: GetState) => {
    const state = getState();
    const environment = currentEnvironmentSelector(state).get('entity');
    const envKey = environment.key;

    const clause = createClause({
      attribute: 'segmentMatch',
      op: 'segmentMatch',
      values: List([segmentKey]),
      contextKind,
    });
    const rule = createRule({ clauses: List([clause]) });

    let flag = flagManagerSelector(state).get('modified') as Flag;
    flag = flag.updateConfiguration(envKey, (cfg) => cfg.update('rules', (rs) => rs.unshift(rule)));
    flag = flag.setRuleVariation(envKey, rule, variation);

    dispatch(changeFlagConfiguration(flag, envKey));
  };

function duplicateRule(flag: Flag, environmentKey: string, rule: Rule) {
  const { updatedFlag } = flag.duplicateRule(environmentKey, rule);

  return changeFlagConfiguration(updatedFlag, environmentKey);
}

function addRuleAtIndex(
  flag: Flag,
  environmentKey: string,
  index: number,
  defaultClause?: Clause | Clause[],
  newRule?: Rule,
) {
  // add rule to the end of the rules list
  const updatedFlag = flag.addRule(environmentKey, defaultClause, newRule);
  const rule = nullthrows(nullthrows(updatedFlag.getRules(environmentKey)).last<undefined>());

  // move newly added rule to specified index
  let reorderedFlag = updatedFlag.setRuleIndex(environmentKey, nullthrows(rule._key), index);

  if (flag.isMigrationFlag()) {
    reorderedFlag = reorderedFlag
      .setRuleBucket(environmentKey, rule, 'key', flag.getMigrationContextKind() || ContextKindKey.USER)
      .setRuleRollout(environmentKey, rule, flag.variations.first(), 100000);
  }

  return changeFlagConfiguration(reorderedFlag, environmentKey);
}

function setRuleVariation(draftFlag: Flag, environmentKey: string, rule: Rule, variation: Variation) {
  let newFlag = draftFlag.setRuleVariation(environmentKey, rule, variation);
  let newRule = nullthrows(newFlag.getRuleById(environmentKey, idOrKey(rule)));

  newFlag = newFlag.setRuleReleasePhaseConfig(environmentKey, newRule, undefined);
  newRule = nullthrows(newFlag.getRuleById(environmentKey, idOrKey(rule)));

  return changeFlagConfiguration(newFlag, environmentKey);
}

function addRuleClause(flag: Flag, environmentKey: string, rule: Rule, clause?: Clause) {
  const updatedFlag = flag.addRuleClause(environmentKey, rule, clause);

  return changeFlagConfiguration(updatedFlag, environmentKey);
}

function discardSpecificConfigChanges(flag: Flag, environmentKey: string) {
  return {
    type: 'flags/DISCARD_SPECIFIC_CONFIG_CHANGES',
    flag,
    environmentKey,
  } as const;
}

function resetVariations() {
  return {
    type: 'flags/RESET_VARIATIONS',
  } as const;
}

function initializeVariationManager(flag: Flag) {
  return {
    type: 'flags/INITIALIZE_VARIATION_MANAGER',
    flag,
  } as const;
}

function destroyVariationManager() {
  return {
    type: 'flags/DESTROY_VARIATION_MANAGER',
  } as const;
}

function changeVariations(flag: Flag) {
  return {
    type: 'flags/CHANGE_VARIATIONS',
    flag,
  } as const;
}

const requestDependentFlags = (flagKey: string, projectKey: string) =>
  ({
    type: 'flags/REQUEST_DEPENDENT_FLAGS',
    flagKey,
    projectKey,
  }) as const;

const requestDependentFlagsDone = (flagKey: string, projectKey: string, dependentFlags: FlagAndEnvKeys) =>
  ({
    type: 'flags/RECEIVE_DEPENDENT_FLAGS',
    flagKey,
    projectKey,
    dependentFlags,
  }) as const;

const requestDependentFlagsFailed = (flagKey: string, projectKey: string, error: ImmutableServerError) =>
  ({
    type: 'flags/RECEIVE_DEPENDENT_FLAGS_FAILED',
    flagKey,
    projectKey,
    error,
  }) as const;

function fetchDependentFlags(projectKey: string, flagKey: string) {
  return (dispatch: GlobalDispatch) => {
    dispatch(requestDependentFlags(flagKey, projectKey));
    return new Promise<void>((resolve, reject) => {
      FlagAPI.getDependentFlags(projectKey, flagKey)
        .then((dependentFlags) => {
          dispatch(requestDependentFlagsDone(flagKey, projectKey, dependentFlags));
          resolve();
        })
        .catch((error) => {
          dispatch(requestDependentFlagsFailed(flagKey, projectKey, error));
          reject(error);
        });
    });
  };
}

const updateFlagDebug = (flag: Flag, projectKey: string) =>
  ({
    type: 'flags/UPDATE_FLAG_DEBUG',
    flag,
    projectKey,
  }) as const;

const updateFlagDebugDone = (flag: Flag, projectKey: string) =>
  ({
    type: 'flags/UPDATE_FLAG_DEBUG_DONE',
    flag,
    projectKey,
  }) as const;

const updateFlagDebugFailed = (flag: Flag, projectKey: string, error: ImmutableServerError) =>
  ({
    type: 'flags/UPDATE_FLAG_DEBUG_FAILED',
    flag,
    projectKey,
    error,
  }) as const;

// This is a hack to keep reducers in sync with the new shell. We do this to avoid HTTP 409
// if the user saves a project setting change after they've updated the maintainer or tags,
// while the UI that uses this reducer is mounted.
const syncFlagWithRestAPI = (projectKey: string, flag: FeatureFlag) =>
  ({
    type: 'flags/SYNC_FLAG_WITH_REST_API',
    projectKey,
    flag,
  }) as const;

function setRuleReleasePhaseConfig(
  originalFlag: Flag,
  draftFlag: Flag,
  environmentKey: string,
  rule: Rule,
  config?: ReleasePhaseConfig,
  configVariationIds: Partial<ConfigVariationIds> = {},
) {
  const { controlVariation, testVariation } = getControlAndTestVariations(
    originalFlag,
    draftFlag,
    environmentKey,
    rule,
  );

  const ruleId = nullthrows(rule._id || rule._key);
  const controlVariationId = nullthrows(configVariationIds?.controlVariationId || controlVariation?._id);
  const testVariationId = nullthrows(configVariationIds?.testVariationId || testVariation?._id);

  let updatedFlag = ensureFlagOnForReleasePhase(draftFlag, environmentKey, config)?.updatedFlag;

  // Auto-enable the rule if it's disabled (rollouts can't run on disabled rules)
  if (disabledRules() && rule.disabled) {
    updatedFlag = updatedFlag.enableRule(environmentKey, rule);
  }

  const newTestVariation: Variation = nullthrows(
    updatedFlag.getDefaultRolloutVariation(controlVariationId, testVariationId),
  );

  updatedFlag = updatedFlag.updateConfiguration(environmentKey, (draftFlagConfig) => {
    const ruleIndex = updatedFlag.getRuleIndex(environmentKey, ruleId);
    const newTestVariationIndex = updatedFlag.variations.indexOf(newTestVariation);
    return draftFlagConfig.setIn(['rules', ruleIndex, 'variation'], newTestVariationIndex);
  });

  updatedFlag = clearPercentageRollout(updatedFlag, environmentKey, rule);
  return changeFlagConfiguration(updatedFlag.setRuleReleasePhaseConfig(environmentKey, rule, config), environmentKey);
}

function ensureFlagOnForReleasePhase(
  draftFlag: Flag,
  environmentKey: string,
  config?: ReleasePhaseConfig,
): { updatedFlag: Flag } {
  let updatedFlag = draftFlag;

  // If we're adding a release config, ensure that the flag is on.
  if (config && !draftFlag.isOn(environmentKey)) {
    updatedFlag = draftFlag.updateConfiguration(environmentKey, (draftFlagConfig) => draftFlagConfig.set('on', true));
  }

  return {
    updatedFlag,
  };
}

export {
  addRuleAtIndex,
  addRuleClause,
  addSegmentRule,
  changeVariations,
  destroyVariationManager,
  duplicateRule,
  editProjectFlagSettings,
  editRuleExclusion,
  fetchDependentFlags,
  fetchFlagByKeyForCurrentProject as fetchFlag,
  fetchFlagIfNeeded,
  fetchFlagsIfNeeded as fetchFlagsForProject,
  fetchFlagStatusesForEnvironment,
  fetchFlag as forceFetchFlag,
  initializeVariationManager,
  modifySegmentRule,
  resetVariations,
  setFallthroughBucket,
  setFallthroughRollout,
  setFallthroughVariation,
  setProgressiveRolloutConfig,
  setRuleReleasePhaseConfig,
  setRuleVariation,
  syncFlagWithRestAPI,
  updateFlag,
  updateProjectFlagSettings,
  updateRuleExclusion,
};

const FlagActionCreators = {
  changeFlagConfiguration,
  changeVariations,
  checkAccessResource,
  clearOffVariation,
  destroyVariationManager,
  editProjectFlagMaintainerAction,
  editProjectFlagSettingsAction,
  editRuleExclusionAction,
  initializeConfigurationManager,
  initializeProjectFlagMaintainer,
  initializeVariationManager,
  requestDeleteFlagDone,
  requestDependentFlags,
  requestDependentFlagsDone,
  requestDependentFlagsFailed,
  requestFlag,
  requestFlagDone,
  requestFlagFailed,
  requestFlags,
  requestFlagsDone,
  requestFlagsFailed,
  requestFlagsAborted,
  requestFlagStatus,
  requestFlagStatusDone,
  requestFlagStatuses,
  requestFlagStatusesDone,
  requestFlagStatusesFailed,
  requestFlagStatusFailed,
  requestFlagStatusesByEnvKeysAndFlagKeys,
  requestFlagStatusesByEnvKeysAndFlagKeysDone,
  requestFlagStatusesByEnvKeysAndFlagKeysFailed,
  resetConfiguration,
  resetVariations,
  selectOffVariation,
  updateFlagDebug,
  updateFlagDebugDone,
  updateFlagDebugFailed,
  updateFlagRequest,
  updateFlagRequestDone,
  updateFlagRequestFailed,
  updateProjectFlagSettingsRequest,
  updateProjectFlagSettingsRequestDone,
  updateProjectFlagSettingsRequestFailed,
  updateRuleExclusionRequest,
  updateRuleExclusionRequestDone,
  updateRuleExclusionRequestFailed,
  discardSpecificConfigChanges,
  syncFlagWithRestAPI,
};

export type FlagAction = GenerateActionType<typeof FlagActionCreators>;
